import React, { useContext, useEffect, useState } from "react";
import { Context } from "../../state/context";
import useFetch from "../../api/fetch";
import { ROLE } from '../../enums/roles';
import { hasRole } from '../../utils/hasRole';

import ReactQuill, {Quill} from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import AuditList from '../audit-list/audit-list.component';

import './translation-form.styles.scss';

const MAX_CHAR_COUNT = 1000;

let Inline = Quill.import('blots/inline');

class BoldBlot extends Inline { }
BoldBlot.blotName = 'bold';
BoldBlot.tagName = 'b';
Quill.register('formats/bold', BoldBlot);

const formats = ["bold", "italic", "underline", "link"];
const modules = {
    toolbar: [
        ["bold", "italic", "underline", "link"],
    ]
};

export default function TranslationForm({ sourceText, targetText, status, charLimit, lastTranslation, auditList, handleSubmit, error }) {
    const { state, dispatch } = useContext(Context);
    const [localText, setLocalText] = useState(targetText);
    const [roles, setRoles] = useState([]);
    const [displaySave, setDisplaySave] = useState(false);
    const [displaySubmitted, setDisplaySubmitted] = useState(false);
    const [displayAdminControls, setDisplayAdminControls] = useState(false);
    const [displayValidate, setDisplayValidate] = useState(false);
    const [displayReject, setDisplayReject] = useState(false);
    const [isReadonly, setIsReadonly] = useState(true);
    const [lengthOverreached, setLengthOverreached] = useState(false);
    const [checkBtnText, setCheckBtnText] = useState("Check with Google translate");
    const [checkStatus, setCheckStatus] = useState(null);

    const fetch = useFetch();

    useEffect(() => {
        setLocalText(targetText);
        setDisplayAdminControls(hasRole(roles, ROLE.ADMIN) && targetText && targetText.length > 0 && status != "0");
    }, [targetText, status]);

    useEffect(() => {
        setDisplayValidate(displayAdminControls && status != "2");
        setDisplayReject(displayAdminControls && status != "3");
        setIsReadonly(!hasRole(roles, ROLE.ADMIN) && status == "2");
    }, [displayAdminControls, status]);

    useEffect(() => {
        setCheckStatus(null);
        let sanitizedText = (localText || '').replace(/(<p>)/g, '');
        sanitizedText = sanitizedText.replace(/(<\/p>|<br>|<br\/>)/g, '\n');

        const index = sanitizedText.lastIndexOf('\n');
        if(index !== -1) {
            sanitizedText = sanitizedText.substring(0, index);
        }

        const lim = charLimit > 0 ? charLimit : MAX_CHAR_COUNT;
        if(sanitizedText.length > lim) {
            setLengthOverreached(true);
        }

        setDisplaySave(sanitizedText && sanitizedText !== targetText && sanitizedText.replace(/\s/g, '').length > 0);
    }, [localText]);

    useEffect(() => {
        setRoles(state.roles)
    }, [state.roles]);

    const handleSave = async (status) => {

        let sanitizedText = localText.replace(/(<p>)/g, '');
        sanitizedText = sanitizedText.replace(/(<\/p>|<br>|<br\/>)/g, '\n');

        const index = sanitizedText.lastIndexOf('\n');
        if(index !== -1) {
            sanitizedText = sanitizedText.substring(0, index);
        }

        const result = await handleSubmit(sanitizedText, status);

        if ((status == 1 || (hasRole(roles, ROLE.ADMIN) && status == 2)) && result.id) {
            setDisplaySubmitted(true);
            setDisplaySave(false);
            setTimeout(() => {
                setDisplaySubmitted(false);
            }, 1000);
        }

      dispatch({ type: 'SET_REFRESH_TRANSLATIONS', translationStatus: true });
      window.$('#confirm-reject').modal("hide");
    }

    const handleVerify = async () => {
        setCheckBtnText('Processing...');
        if(checkStatus) {
            setCheckStatus(null);
        }

        const result = await fetch(`/api/value/verify/${state.language}/${state.activeKey.id}`).then(x => x.json()).catch((e) => { return false });

        if(result) {
            if(result.valid) {
                setCheckBtnText("Check with Google translate");
                setCheckStatus({ status: 'ok', message: 'The text was successfully verified.'});
            } else {
                setCheckBtnText("Check with Google translate");
                setCheckStatus({ status: 'fail', message: 'The text did not match translations.', englishToTargetResults: result.englishToTargetResults, targetToEnglishResults: result.targetToEnglishResults });
            }
        } else {
            setCheckBtnText("Check with Google translate");
            setCheckStatus({ status: 'fail', message: 'There was an error trying to process the request.'});
        }
    }

    const handleTranslationChange = (value, delta, source, editor) => {
        const lim = charLimit > 0 ? charLimit : MAX_CHAR_COUNT;
        const sanitizedValue = value.replace(/<\/?[^>]+(>|$)/g, '')
        if(sanitizedValue.length <= lim) {
            setLocalText(value);
        }

        setLengthOverreached(sanitizedValue.length > lim)
    }

    return (
        <div className="mb-4">
            <h6 className="translation-section-title translation-form-title text-muted">Translate the following text</h6>
            <div className="translation-source-text translation-text-box" dangerouslySetInnerHTML={{ __html: sourceText }} />

            {(lastTranslation && lastTranslation.length > 0) && <h6 className="translation-section-title translation-form-title text-muted">Last verified translation</h6>}
            {(lastTranslation && lastTranslation.length > 0) && <div className="translation-verified-text translation-text-box" dangerouslySetInnerHTML={{ __html: lastTranslation }} />}


            <h6 className="translation-section-title translation-form-title text-muted">Add translation</h6>
            <ReactQuill
                theme="snow"
                defaultValue={localText ? localText.replace(/(\n)/g, '<br/>') : ""}
                value={localText ? localText.replace(/(\n)/g, '<br/>') : ""}
                placeholder={"Add translation here"}
                rows={3}
                modules={modules}
                formats={formats}
                readOnly={isReadonly}
                onChange={handleTranslationChange}/>

            <div className="d-flex align-items-center justify-content-end text-muted"><small><span className={`font-weight-bold${lengthOverreached ? ' text-danger': ''} `}>{(localText || '').replace(/<\/?[^>]+(>|$)/g, '').length}</span>/{charLimit > 0 ? charLimit : MAX_CHAR_COUNT} characters</small></div>


            {error && <div className="alert alert-danger mt-2" role="alert">{error.message}</div>}
            {lengthOverreached && <div className="alert alert-danger mt-2" role="alert">You exceeded the characters limit. Please make sure you keep the translation under {charLimit > 0 ? charLimit : MAX_CHAR_COUNT} characters.</div>}

            {!isReadonly && <div className="d-flex justify-content-between align-items-start mt-2">
                <div className="status-container">
                    {displayValidate && <button className={`btn btn-mondly btn-green cursor-pointer mb-0 mr-2`} onClick={() => handleSave(2)}>Approve translation</button>}
                    {displayReject && <button className={`btn btn-mondly btn-reject cursor-pointer ${displayValidate ? 'ml-1' : ''} mb-0 mr-3`} disabled={!displayReject} data-toggle="modal" data-target="#confirm-reject">Reject translation</button>}

                    {(!hasRole(roles, ROLE.ADMIN) && !lengthOverreached) && <button className={`btn btn-mondly mb-0${displaySave ? ' btn-orange cursor-pointer' : ((displaySubmitted && !displaySave) ? ' btn-green cursor-pointer' : ' transparent')}`}
                                                              disabled={!displaySave || lengthOverreached}
                                                              onClick={() => handleSave(1)}>{(displaySubmitted && !displaySave) ? 'Submitted' : 'Submit for review'}</button>}
                    {(hasRole(roles, ROLE.ADMIN)) && <button className={`btn btn-mondly mb-0${displaySave ? ' btn-green cursor-pointer' : ((displaySubmitted && !displaySave) ? ' btn-green cursor-pointer' : ' transparent')}`}
                                                              disabled={!displaySave || lengthOverreached}
                                                              onClick={() => handleSave(2)}>{(displaySubmitted && !displaySave) ? 'Submitted' : 'Save translation'}</button>}
                    {(hasRole(roles, ROLE.ADMIN)) && <button className={`btn btn-mondly btn-blue mb-0 cursor-pointer`}
                                                              onClick={() => handleVerify()}>{checkBtnText}</button>}
                </div>



                {(hasRole(roles, ROLE.ADMIN) && auditList.length > 0) && <a className="audit-link" data-toggle="collapse" href="#collapsed-history" role="button" aria-expanded="false" aria-controls="collapsed-history">Show previous versions <i className="fas fa-chevron-down ml-1"></i></a>}

            </div>}
            {(hasRole(roles, ROLE.ADMIN) && checkStatus) && <div className={`mt-3 alert alert-${checkStatus.status && checkStatus.status == 'ok' ? 'success' : 'danger'}`} role="alert">
                <button type="button" className="close" aria-label="Close" onClick={e => setCheckStatus(null)}>
                    <span aria-hidden="true">&times;</span>
                </button>
                <p>{checkStatus.message}</p>
                {(checkStatus.status == 'fail' && checkStatus.targetToEnglishResults && checkStatus.targetToEnglishResults.length > 0) && <>
                    <p>Submitted text translates to:</p>
                    <ul>
                        {checkStatus.targetToEnglishResults.map((targetToEnglishResult, index) =>
                            <li key={`english-translation-${index}`}>
                                <p>{targetToEnglishResult.translation}</p>
                            </li>)}
                    </ul>
                </>}
                {(checkStatus.status == 'fail' && checkStatus.englishToTargetResults && checkStatus.englishToTargetResults.length > 0) && <>
                    <hr />
                    <p>Google suggested translations:</p>
                    <ul className="mb-0">
                        {checkStatus.englishToTargetResults.map((englishToTargetResult, index) =>
                            <li key={`target-translation-${index}`}>
                                <p>{englishToTargetResult.translation}</p>
                                {(englishToTargetResult.transliteration && englishToTargetResult.transliteration.length > 0) && <p>({englishToTargetResult.transliteration})</p>}
                            </li>)}
                    </ul>

                </>}
            </div>}
            {(hasRole(roles, ROLE.ADMIN) && auditList.length > 0) && <AuditList auditList={auditList} />}

            <div className="modal fade" id="confirm-reject" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-lg modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-12">
                                    <p>Are you sure you want to reject this translation?</p>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-mondly btn-outline-blue" data-dismiss="modal" aria-label="Close">Cancel</button>
                            <button className="btn btn-mondly btn-blue" onClick={() => handleSave(3)}>Reject</button>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    )
}
